import React, { Component } from 'react'
import { csrfToken, parseHash, removeHash } from './utils/auth'
import subscriptionOptions from './utils/subscriptionOptions'
import loginButton from './assets/netlify-login-button.svg'
import './App.css'

// For local UI work
const localDev = process.env.NODE_ENV === 'development'

/**
 * subscriptionData maps to customer.io attributes
 {
   // All emails
   "unsubscribed": true,
   // welcome emails
   "unsubscribed_from_welcome": true,
   // event emails
   "unsubscribed_from_events": true,
   // blog emails
   "unsubscribed_from_blog": true,
   // product emails
   "unsubscribed_from_product": true,
   // community emails
   "unsubscribed_from_community": true,
 }
 */

/* For customer.io events */
const EVENTS = {
  unsubscribe: 'email:unsubscribe',
  resubscribe: 'email:resubscribe',
  managePreferences: 'email:managePreferences',
}

const subscriptionData = [{
  'type': 'welcome',
  'text': 'New user emails',
  'attribute': 'unsubscribed_from_welcome',
  'frequency': 'About 5 per month for new users',
  'description': 'Emails helping new Netlify users get acquainted with app.netlify.com',
  'checked': false
}, {
  'type': 'events',
  'text': 'Events',
  'attribute': 'unsubscribed_from_events',
  'frequency': 'About 3 per year',
  'description': 'Announcing new and upcoming events hosted by Netlify',
  'checked': false
},
{
  'type': 'product',
  'text': 'Product updates',
  'attribute': 'unsubscribed_from_product',
  'frequency': 'About 6 per year',
  'description': 'Announcing major features or changes to Netlify',
  'checked': false
},
{
  'type': 'blog',
  'text': 'Blog digest',
  'attribute': 'unsubscribed_from_blog',
  'frequency': 'About 1 per month',
  'description': 'Monthly collection of our favorite blog content from the past 30 days',
  'checked': false
},
{
  'type': 'community',
  'text': 'Community digest',
  'attribute': 'unsubscribed_from_community',
  'frequency': 'About 1 per month',
  'description': 'Monthly recap of interesting posts, as well as a look at the JAMstack community',
  'checked': false
},
{
  'type': 'all',
  'text': 'All Emails',
  'attribute': 'unsubscribed',
  'description': 'Check this box to unsubscribe from all emails',
  'checked': false
},
// ... add more types here.
]

export default class App extends Component {
  constructor (props, context) {
    super(props, context)
    // console.log('window.location.hash', window.location.hash)
    const data = parseHash(window.location.hash, true)
    // console.log('parseHash', data)

    /* Catch users who don’t exist in customer.io for whatever reason */
    if (data.id && data.action && data.action === 'identify') {
      waitForGlobal('_cio', () => {
        const identifyPayload = {
          id: data.id,
        }
        if (data.email) {
          identifyPayload.email = data.email
        }
        window._cio.identify(identifyPayload)
      })
    }

    /* Clear hash */
    setTimeout(() => removeHash(), 0)

    /* Protect against csrf (cross site request forgery https://bit.ly/1V1AvZD) */
    if (data.token && !localStorage.getItem(data.csrf)) {
      alert('Token invalid. Please try to login again')
      return
    }
    /* Clean up csrfToken */
    localStorage.removeItem(data.csrf)

    /* populate settings */
    const options = (!data.id) ? [] : subscriptionOptions(data, subscriptionData)
    console.log('initial', options)
    const types = getTypes(options)
    console.log('initialTypes', types)
    this.state = {
      data: data,
      currentSettings: types,
      options: (localDev) ? subscriptionOptions(data, subscriptionData) : options,
      loading: false,
    }
  }
  /* Handle Netlify OAuth */
  handleAuth = e => {
    e.preventDefault()
    const token = csrfToken()
    const { location, localStorage } = window
    /* Set csrf token */
    localStorage.setItem(token, 'true')
    /* Do redirect */
    const redirectTo = `${location.origin}${location.pathname}`

    window.location.href = `/api/auth?url=${redirectTo}&csrf=${token}`
  }
  handlecheckboxCheckbox = (event) => {
    const { target } = event
    const { options } = this.state
    const checkboxCompleted = target.checked
    const type = target.dataset.type
    const newOptions = options.map((opt) => {
      // Remove check from 'all' if something toggled on
      if (type !== 'all' && checkboxCompleted === false && opt.type === 'all') {
        return {
          ...opt,
          checked: false
        }
      }
      if (opt.type === type || type === 'all') {
        return {
          ...opt,
          checked: checkboxCompleted
        }
      }
      return opt
    })
    this.setState({
      options: newOptions
    })
  }
  handleUpdate = (e) => {
    e.preventDefault()
    const { options, data, currentSettings } = this.state
    const { id, nonce } = data

    if (!id) {
      alert('Unknown user. Please contact david.wells@netlify.com')
    }

    this.setState({
      loading: true
    })

    const payload = options.map((opt) => {
      return {
        attribute: opt.attribute,
        checked: opt.checked
      }
    })

    // support legacy links
    const urlUpdate = (nonce) ? '/api/update' : '/.netlify/functions/update-preferences'
    fetch(urlUpdate, {
      method: 'POST',
      body: JSON.stringify({
        id: id,
        nonce: nonce,
        attributes: payload
      })
    })
    .then((resp) => resp.json())
    .then((resp) => {
      const apiResponse = resp || {}
      if (apiResponse.error) {
        return handleApiErrors(apiResponse.error)
      }

      const newSettings = getTypes(options)
      waitForGlobal('_cio', () => {
        // identify customer before tracking event
        if (localDev) {
          console.log('identify', { id: id })
        } else {
          window._cio.identify({ id: id })
        }

        // Format payload for customer.io track call
        const change = calculateChange(currentSettings, newSettings)
        const eventPayload = {
          campaignId: data.cid || 'NA',
          messageId: data.mid || 'NA',
          source: (!data.mid && !data.cid) ? 'direct' : 'email',
          from: currentSettings,
          to: newSettings,
          completeOptOut: (newSettings.length === options.length)
        }

        if (change === 'positive') {
          if (localDev) {
            console.log(EVENTS.resubscribe, eventPayload)
          } else {
            // Send resubscribe event
            window._cio.track(EVENTS.resubscribe, eventPayload)
          }
        }

        if (change === 'negative') {
          if (localDev) {
            console.log(EVENTS.unsubscribe, eventPayload)
          } else {
            // Send unsubscribe event
            window._cio.track(EVENTS.unsubscribe, eventPayload)
          }
        }

        const managePayload = Object.assign({}, eventPayload, {
          change: change,
        })
        if (localDev) {
          console.log(EVENTS.managePreferences, managePayload)
        } else {
          // Send managePreferences event
          window._cio.track(EVENTS.managePreferences, managePayload)
        }
      })
      this.setState({
        loading: false,
        message: 'Successfully updated email preferences!',
        currentSettings: newSettings,
      })
      setTimeout(() => {
        this.setState({
          message: null
        })
      }, 1500)
    })
    .catch((e) => {
      console.log('error', e)
      this.setState({
        loading: false,
        message: 'There was an error with the request. Please reload the page and try again'
      })
      setTimeout(() => {
        this.setState({
          message: null
        })
      }, 4000)
    })
  }
  renderOptions() {
    const { options } = this.state
    const optionsRender = options.map((opt, i) => {
      const boxIcon = (opt.checked) ? '#checkbox__box__done' : '#checkbox__box'
      const { type } = opt
      return (
        <div key={i} className='checkbox-item'>
          <label className="checkbox">
            <input
              data-type={type}
              className="checkbox__state"
              type="checkbox"
              onChange={this.handlecheckboxCheckbox}
              checked={opt.checked}
            />
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 25" className="checkbox__icon">
              <use xlinkHref={`${boxIcon}`} className="checkbox__box"></use>
              <use xlinkHref="#checkbox__check" className="checkbox__check"></use>
            </svg>
            <div className='checkbox-label'>
              <div className='checkbox-label-title'>
                {opt.text} <span className='frequency'>{opt.frequency}</span>
              </div>
              <div className='checkbox-label-description'>
                {opt.description}
              </div>
            </div>
          </label>
        </div>
      )
    })

    return (
      <div className='checkbox-list'>
        {optionsRender}
      </div>
    )
  }
  render() {
    const { data, loading, message } = this.state
    const loggedIn = data.nonce || data.id ||  false
    const email = data.email

    let contents = (
      <div>
        <p>Please login to Netlify to update your email preferences</p>
        <button onClick={this.handleAuth} >
          <img alt='login to netlify' className='login-button' src={loginButton} />
        </button>
      </div>
    )
    if (localDev || loggedIn) {
      const handler = (loading) ? () => {} : this.handleUpdate
      const buttonText = (loading) ? 'Submitting changes...' : 'Opt out of checked emails'
      let copy = (!email) ? '' : <span> for <b>{censorEmail(email)}</b></span>

      contents = (
        <div>
          <p>Help us improve your experience with Netlify through better communication.<br/><br/>

          Please adjust your email preferences{copy}<br/><br/>

          Check the boxes below to <b>opt out</b> of emails you no longer wish to recieve.
          </p>
          <div className='checkbox-wrapper'>
            {this.renderOptions()}
            <div>
              <button className='primary-button' onClick={handler}>
                {buttonText}
              </button>
            </div>
            <div className='message'>
              {message}
            </div>
          </div>
        </div>
      )
    }

    return (
      <div className="app">
        <h1>Netlify Email Preferences</h1>
        {contents}
      </div>
    )
  }
}

function handleApiErrors(errMsg) {
  switch (errMsg) {
    case 'invalid credentials':
    case 'Missing nonce':
    case 'Missing user id':
    case 'invalid user id':
      console.log(errMsg)
      alert('Please login to complete your request')
      setTimeout(() => {
        window.location.href = window.location.origin
      }, 1000)
      break;
    case 'session timeout':
      console.log(errMsg)
      alert('For security reasons, your session has timed out. Please login to complete your request')
      setTimeout(() => {
        window.location.href = window.location.origin
      }, 1000)
      break;
    default:
      console.log('Error', errMsg);
  }
}

function calculateChange(oldSettings, newSettings) {
  const oldLength = oldSettings.length
  const newLength = newSettings.length
  if (oldLength > newLength) {
    return 'positive'
  }
  if (oldLength < newLength) {
    return 'negative'
  }
  return 'neutral'
}

function getTypes(options) {
  return options.filter((opt) => {
    return opt.checked
  }).map((opt) => {
    return opt.attribute
  })
}

function censorWord(str) {
  const splitAt = Math.floor(str.length / 2)
  const firstChars = str.substring(0, splitAt)
  const other = firstChars + "*".repeat(str.length - splitAt) + str.slice(-1);
  return other
}

function censorEmail(email) {
  const arr = email.split("@")
  return censorWord(arr[0]) + "@" + arr[1]
}

function waitForGlobal(key, callback) {
  if (window[key]) {
    return callback()
  }
  setTimeout(() => {
    waitForGlobal(key, callback);
  }, 100)
}
